import React, { useState, useRef, useEffect } from 'react'
import theme from '../config/theme'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { validateFile, formatFileSize } from '../utils/fileValidation'

interface FileUploadProps {
  onFileSelect: (file: File) => void
  accept?: string
  maxSize?: number // in MB
  className?: string
  disabled?: boolean
  selectedFile?: File | null
  onFileRemove?: () => void
  /** Enable strict validation including MIME type and magic bytes check */
  strictValidation?: boolean
  /** Custom error handler - required for showing validation errors */
  onValidationError?: (error: string) => void
  /** Hide the default file requirements text (useful when showing custom requirements) */
  hideRequirements?: boolean
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  accept = '.jpg,.jpeg,.png',
  maxSize = 1,
  className = '',
  disabled = false,
  selectedFile = null,
  onFileRemove,
  strictValidation = true,
  onValidationError,
  hideRequirements = false
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Create image preview when file is selected
  useEffect(() => {
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(selectedFile)
      setImagePreview(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setImagePreview(null)
    }
  }, [selectedFile])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled && !isValidating) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    if (disabled || isValidating) return

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      await handleFileValidation(files[0])
    }
  }

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      await handleFileValidation(files[0])
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const showError = (error: string) => {
    if (onValidationError) {
      onValidationError(error)
    } else {
      // Show error inline when no callback provided
      setInternalError(error)
      console.warn('[FileUpload] Validation error:', error)
    }
  }

  // Clear internal error when file is successfully selected
  const clearError = () => {
    setInternalError(null)
  }

  const handleFileValidation = async (file: File) => {
    if (strictValidation) {
      setIsValidating(true)
      try {
        // Parse allowed extensions from accept prop
        const allowedExtensions = accept
          .split(',')
          .map(ext => ext.trim().toLowerCase())
          .filter(ext => ext.startsWith('.'))

        // Calculate max size in bytes
        const maxSizeBytes = maxSize * 1024 * 1024

        // Use comprehensive validation
        const result = await validateFile(file, {
          maxSizeBytes,
          allowedExtensions: allowedExtensions.length > 0 ? allowedExtensions : undefined,
          validateMagicBytes: true
        })

        if (!result.valid) {
          showError(result.error || 'ไฟล์ไม่ถูกต้อง')
          return
        }

        clearError()
        onFileSelect(file)
      } finally {
        setIsValidating(false)
      }
    } else {
      // Legacy validation (extension and size only)
      if (!validateFileLegacy(file)) {
        return
      }
      clearError()
      onFileSelect(file)
    }
  }

  // Legacy validation for backward compatibility
  const validateFileLegacy = (file: File): boolean => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      showError(`ไฟล์มีขนาดใหญ่เกินไป (สูงสุด ${maxSize}MB)`)
      return false
    }

    // Check file type by extension
    const allowedTypes = accept.split(',').map(type => type.trim().toLowerCase())
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()

    if (!allowedTypes.includes(fileExtension)) {
      showError(`ประเภทไฟล์ไม่ถูกต้อง รองรับเฉพาะ: ${allowedTypes.join(', ')}`)
      return false
    }

    return true
  }

  const handleClick = () => {
    if (!disabled && !isValidating) {
      fileInputRef.current?.click()
    }
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onFileRemove) {
      onFileRemove()
    }
  }

  return (
    <div className={className}>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
          isDragOver ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
        } ${disabled || isValidating ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{
          borderColor: isDragOver ? theme.brandColor.red : theme.color.light500,
          backgroundColor: isDragOver ? theme.brandColor.red + '10' : 'transparent'
        }}
      >
        <div className="flex flex-col items-center space-y-4">
          {isValidating ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.brandColor.red }}></div>
              <p className="text-lg" style={{ color: theme.color.gray500 }}>
                กำลังตรวจสอบไฟล์...
              </p>
            </>
          ) : (
            <>
              <img
                src="/images/icons/upload.png"
                alt="Upload"
                className="w-12 h-12"
              />
              <div>
                <p className="text-lg" style={{ color: theme.color.gray500 }}>
                  Drag your file(s) to start uploading
                </p>

                <div className="flex items-center justify-center w-full my-2">
                  <div className="flex-1 h-px" style={{
                    background: theme.color.light
                  }}></div>
                  <span className="px-3 text-sm uppercase" style={{ color: theme.color.light }}>
                    OR
                  </span>
                  <div className="flex-1 h-px" style={{
                    background: theme.color.light
                  }}></div>
                </div>
                <button
                  type="button"
                  className="mt-2 px-4 py-2 rounded-lg border"
                  style={{
                    borderColor: theme.color.light,
                    backgroundColor: theme.color.light100,
                    color: theme.color.light
                  }}
                  disabled={disabled || isValidating}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClick()
                  }}
                >
                  Browse files
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled || isValidating}
      />

      {/* Uploaded File Display */}
      {selectedFile && (
        <div className="mt-4 flex items-center justify-between p-3 rounded-lg"
        style={{borderColor: theme.color.light300, borderWidth: 1, borderStyle: 'solid' }}>
          <div className="flex items-center space-x-3">
            {imagePreview && (
              <img
                src={imagePreview}
                alt={selectedFile.name}
                className="w-10 h-10 rounded object-cover"
                style={{ backgroundColor: theme.color.light300 }}
              />
            )}
            <div>
              <p className="text-sm" style={{ color: theme.color.gray500 }}>
                {selectedFile.name}
              </p>
              <p className="text-xs" style={{ color: theme.color.gray300 }}>
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </div>
          {onFileRemove && (
            <button
              type="button"
              onClick={handleRemove}
              className="w-6 h-6 rounded-full flex items-center justify-center"
            >
              {React.createElement(IoIosCloseCircleOutline as any, {
                className: "w-6 h-6",
                style: { color: theme.brandColor.red }
              })}
            </button>
          )}
        </div>
      )}

      {/* Inline Error Message (when no onValidationError callback provided) */}
      {internalError && !onValidationError && (
        <div
          className="mt-3 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: `${theme.color.error}15`,
            border: `1px solid ${theme.color.error}40`,
            color: theme.color.error
          }}
        >
          {internalError}
        </div>
      )}

      {/* File Requirements */}
      {!hideRequirements && (
        <div className="mt-4 text-sm space-y-1" style={{ color: theme.color.light }}>
          <div className="flex items-start">
            <span className="mr-2" style={{ color: theme.color.light }}>•</span>
            <span>Max file size: {maxSize} MB</span>
          </div>
          <div className="flex items-start">
            <span className="mr-2" style={{ color: theme.color.light }}>•</span>
            <span>File formats: {accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')}</span>
          </div>
          <div className="flex items-start">
            <span className="mr-2" style={{ color: theme.color.light }}>•</span>
            <span>Image size: width 2500px * height 843/1686px</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default FileUpload
